import { Box, Grid, GridItem, Heading, ListItem, OrderedList } from '@chakra-ui/react';
import { JSX, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CustomHeading } from 'shared-components';
import { IRecordedInterviewFulfilmentData } from 'types';
import { ApplicationState } from '../../../../store';
import { getApplicantOffer } from '../../../../store/apply/actions';
import { getAllTasksFromRequirements } from '../../../../utilities/transformRequirements';
import { getDecryptedQuestions } from '../../watchInterview/helpers';
import InterviewVideoPlayer from '../../watchInterview/components/InterviewVideoPlayer';
import ApplicantHeader from '../../../molecules/applicantHeader';

export default function WatchApplyInterview(): JSX.Element | null {
  const {
    taskId,
    videoId: videoIdParam,
    userId,
    uni,
    offerId,
    applicationId,
  } = useParams<{
    taskId: string;
    videoId?: string;
    userId: string;
    uni: string;
    offerId: string;
    applicationId: string;
  }>();
  const dispatch = useDispatch();
  const offer = useSelector((state: ApplicationState) => state.apply.offer);

  useEffect(() => {
    if (userId && uni && offerId) {
      dispatch(getApplicantOffer(userId, uni, offerId));
    }
  }, [dispatch, userId, uni, offerId]);

  const decodedTaskId = taskId ? decodeURIComponent(taskId) : '';
  const decodedVideoId = videoIdParam ? decodeURIComponent(videoIdParam) : '';
  const task = getAllTasksFromRequirements(offer?.requirements).find(t => t.id === decodedTaskId);

  // When videoId is provided in the URL, look up the matching historical entry from conditionHistory.
  // Otherwise fall back to the current task fulfilment data.
  const currentFulfilmentData = task?.fulfilmentData as IRecordedInterviewFulfilmentData | null;
  const historicalEntry = decodedVideoId
    ? task?.conditionHistory?.find(
        entry => (entry.fulfilmentData as IRecordedInterviewFulfilmentData | null)?.videoId === decodedVideoId,
      )
    : undefined;
  const fulfilmentData =
    (historicalEntry?.fulfilmentData as IRecordedInterviewFulfilmentData | null) ?? currentFulfilmentData;

  if (!task || !fulfilmentData) {
    return null;
  }

  const { hash, hashTimestamp, videoId, questions: encryptedQuestions } = fulfilmentData;
  const interviewName = task.taskText ?? fulfilmentData.welcomeTitle ?? 'Recorded interview';
  const questions = encryptedQuestions ? getDecryptedQuestions(encryptedQuestions) : [];
  const programmeName = offer?.programme?.courseName ?? '';
  const offerRoute = `/application/${uni}/${userId}/${applicationId}/offers/${offerId}`;

  return (
    <Box height={{ lg: '100vh' }} overflow={{ lg: 'hidden' }} display={{ lg: 'flex' }} flexDirection={{ lg: 'column' }}>
      <Box px={{ base: 4, md: 6, xl: 8, '2xl': 12 }} pt="6" maxWidth="100rem" mx="auto" w="100%">
        <ApplicantHeader
          pageName={interviewName}
          showProgrammesCrumb
          breadcrumbs={[{ name: programmeName, path: offerRoute }]}
        />
      </Box>

      <Grid
        display={{ base: 'block', lg: 'grid' }}
        flex={{ lg: '1' }}
        overflow={{ lg: 'hidden' }}
        maxWidth="100rem"
        mx="auto"
        w="100%"
        templateColumns={{ lg: '35rem 1fr', xl: '40rem 1fr' }}
        templateRows={{ lg: '100%' }}
      >
        <GridItem
          w="100%"
          h="100%"
          overflowY={{ lg: 'auto' }}
          className="nav-scrollbar"
          px={{ base: 4, md: 6, xl: 8, '2xl': 12 }}
          pb="6"
        >
          <CustomHeading level="h1" mb={2}>
            {interviewName}
          </CustomHeading>

          <InterviewVideoPlayer hashTimestamp={hashTimestamp} hash={hash} videoId={videoId} />
        </GridItem>

        <GridItem
          w="100%"
          h="100%"
          overflowY={{ lg: 'auto' }}
          className="nav-scrollbar"
          pl={4}
          pr={{ base: 4, lg: 6, xl: 8, '2xl': 12 }}
          py={4}
        >
          <Heading as="h2" fontSize="xl" mt="2" mb="6">
            Questions
          </Heading>

          <OrderedList spacing={4}>
            {questions.map((q, index) => {
              const questionText = Array.isArray(q.question) ? q.question.join(' ') : q.question;
              return <ListItem key={index}>{questionText}</ListItem>;
            })}
          </OrderedList>
        </GridItem>
      </Grid>
    </Box>
  );
}
